import { useMemo } from 'react';

import { Badge } from 'cpool-components/general/Badge';
import { Tooltip, TooltipContent, TooltipTrigger } from 'cpool-components/general/Tooltip';
import { Truncate } from 'cpool-components/general/Truncate';
import type { LOANREQUEST_STATUS, LOANREQUEST_STATUS_REASON } from 'cpool-constants';
import { AGGREGATE_NAMES, EMPTY_PLACEHOLDER, LOANREQUEST_STATUSES } from 'cpool-constants';
import type {
    SchemaLoanRequestResolver_Find,
    SchemaLoanRequestResolver_GetDuplicateInfoByLead,
    SchemaLoanRequestResolver_GetSummary,
} from 'cpool-tables/backend-resolvers';
import { useTranslation } from 'react-i18next';
import { LuAlarmClock } from 'react-icons/lu';

import { LEAD_LIST_ROW_HIGHLIGHT } from '../../rowHighlight';
import { type RowHighlightProps, useRowHighlightDialog } from '../../useRowHighlightDialog';
import { SubstatusChangeModal } from '~/components/SubstatusChangeModal';
import { useStatusColorsEnabled } from '~/hooks/useStatusColorsEnabled';
import { dateTimeTwoDigitFormat } from '~/lib/i18n.ts';
import { buildAclPermissions } from '~/utils/buildAclPermissions';
import { cn } from '~/utils/cnTailwind';
import { LOANREQUEST_STATUS_COLORS } from '~/utils/loanRequestStatusColors';
import { loanRequestStatusesWithSubStatuses } from '~/utils/loanRequestStatusesWithSubStatuses';

const PERMISSIONS_FAIL_CLOSED = buildAclPermissions(false);

type Props = {
    lead:
        | SchemaLoanRequestResolver_GetSummary[number]
        | (SchemaLoanRequestResolver_Find[number] & {
              antragsteller1: NonNullable<SchemaLoanRequestResolver_Find[number]['antragsteller1']>;
          })
        | SchemaLoanRequestResolver_GetDuplicateInfoByLead[number];
} & RowHighlightProps;

export const StatusInfo: React.FC<Props> = ({ lead, ...highlightProps }) => {
    const { permissions = PERMISSIONS_FAIL_CLOSED, resubmission, status, statusReason, subStatus } = lead;
    const { i18n, t } = useTranslation();
    const statusColorsEnabled = useStatusColorsEnabled();
    const { dialogOpen: modalOpen, setDialogOpen: setModalOpen } = useRowHighlightDialog(
        LEAD_LIST_ROW_HIGHLIGHT.STATUS,
        highlightProps,
    );

    const isResubmissionInPast = useMemo(() => {
        if (resubmission === null) return false;
        return Date.now() > resubmission;
    }, [resubmission]);

    const resubmissionLabel = useMemo(() => {
        if (resubmission === null) return null;
        return t('LEADS.TRAIT.RESUBMISSION', {
            date: dateTimeTwoDigitFormat(resubmission),
            time: new Date(resubmission).toLocaleTimeString('de-DE', {
                hour: '2-digit',
                minute: '2-digit',
            }),
        });
    }, [resubmission, t]);

    let translatedStatus: string | null = null;
    let translatedSubStatus: string | null = null;
    let translatedStatusReasons: string | null = null;

    if (status) translatedStatus = t(`STATUS.${status}.STATUS`);

    if (subStatus) {
        const subStatusTranslation = `STATUS.${status}.${subStatus}` as const;
        // @ts-expect-error status schema is a discriminated union, we check if the translation exists instead of checking the type.
        if (i18n.exists(subStatusTranslation)) translatedSubStatus = t(subStatusTranslation);
    }

    if (statusReason && statusReason.length > 0) {
        translatedStatusReasons = statusReason
            .map((reason) => t(`STATUS.REASONS.${reason as LOANREQUEST_STATUS_REASON}`))
            .join(', ');
    }

    const isSubstatusClickable =
        status !== LOANREQUEST_STATUSES.CANCELED &&
        status in loanRequestStatusesWithSubStatuses &&
        permissions.commands[AGGREGATE_NAMES.LOAN_REQUEST].changeStatus;

    const handleSubStatusClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (isSubstatusClickable) setModalOpen(true);
    };

    const tooltipContent = [translatedStatus, translatedSubStatus, translatedStatusReasons, resubmissionLabel]
        .filter(Boolean)
        .join('\n');
    const hasTooltipContent = tooltipContent.trim().length > 0;

    return (
        <div className="flex flex-col justify-center px-3 py-3 min-w-[190px] max-w-[190px]">
            <Tooltip>
                <TooltipTrigger asChild>
                    <div className="flex flex-col gap-1">
                        {statusColorsEnabled && status && translatedStatus ? (
                            <Badge className={cn('self-start max-w-full', LOANREQUEST_STATUS_COLORS[status].pill)}>
                                <Truncate showTooltip={false} maxWidth="130px">
                                    {translatedStatus}
                                </Truncate>
                            </Badge>
                        ) : (
                            <Truncate showTooltip={false} className="text-midnight text-sm" maxWidth="130px">
                                {translatedStatus || EMPTY_PLACEHOLDER}
                            </Truncate>
                        )}
                        {translatedSubStatus && (
                            <span
                                className={
                                    isSubstatusClickable
                                        ? 'text-midnight text-sm cursor-pointer hover:text-midnight-hover underline truncate'
                                        : 'text-midnight text-sm truncate'
                                }
                                onClick={isSubstatusClickable ? handleSubStatusClick : undefined}
                                data-testid={isSubstatusClickable ? 'substatus-clickable' : undefined}
                            >
                                {translatedSubStatus}
                            </span>
                        )}
                        {!translatedSubStatus && isSubstatusClickable && (
                            <span
                                className="text-midnight text-sm cursor-pointer hover:text-midnight-hover underline italic truncate"
                                onClick={handleSubStatusClick}
                                data-testid="substatus-clickable"
                            >
                                {t('STATUS.SET_SUBSTATUS')}
                            </span>
                        )}
                        {status === LOANREQUEST_STATUSES.CANCELED && translatedStatusReasons && (
                            <span className="text-midnight text-sm truncate">{translatedStatusReasons}</span>
                        )}
                        {resubmissionLabel && (
                            <span
                                className={cn(
                                    'flex items-center gap-1 text-xs whitespace-nowrap',
                                    isResubmissionInPast ? 'text-alert-error' : 'text-midnight-placeholder',
                                )}
                                data-testid="status-resubmission"
                            >
                                <LuAlarmClock className="size-[15px] shrink-0" aria-hidden />
                                <span>{resubmissionLabel}</span>
                            </span>
                        )}
                    </div>
                </TooltipTrigger>
                {hasTooltipContent && (
                    <TooltipContent side="bottom" align="start">
                        <div className="flex flex-col gap-1 max-w-[300px]">
                            {tooltipContent.split('\n').map((line, i) => (
                                <span key={i}>{line}</span>
                            ))}
                        </div>
                    </TooltipContent>
                )}
            </Tooltip>
            {isSubstatusClickable && (
                <div onClick={(e) => e.stopPropagation()} role="presentation">
                    <SubstatusChangeModal
                        open={modalOpen}
                        onClose={() => setModalOpen(false)}
                        leadId={lead.id}
                        currentStatus={status as Exclude<LOANREQUEST_STATUS, typeof LOANREQUEST_STATUSES.CANCELED>}
                        currentSubStatus={subStatus}
                        permissions={permissions}
                        resubmission={resubmission}
                    />
                </div>
            )}
        </div>
    );
};
